import { useState } from 'react';
import { ArrowLeft, AlertTriangle, Camera, MapPin, Clock, Upload, CheckCircle2 } from 'lucide-react';

interface IncidentReportScreenProps {
  onBack: () => void;
}

export function IncidentReportScreen({ onBack }: IncidentReportScreenProps) {
  const [incidentType, setIncidentType] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const incidentTypes = [
    { value: 'accident', label: 'Traffic Accident', icon: '🚗' },
    { value: 'theft', label: 'Theft/Robbery', icon: '🔒' },
    { value: 'harassment', label: 'Harassment', icon: '⚠️' },
    { value: 'vehicle', label: 'Vehicle Issue', icon: '🔧' },
    { value: 'customer', label: 'Customer Issue', icon: '👤' },
    { value: 'other', label: 'Other', icon: '📝' },
  ];

  const handlePhotoCapture = () => {
    const mockPhoto = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YzZjNmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM2QjZCNkIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbmNpZGVudCBQaG90bzwvdGV4dD48L3N2Zz4=';
    setPhotos([...photos, mockPhoto]);
  };

  const handleSubmit = () => {
    if (incidentType && description) {
      setSubmitted(true);
      setTimeout(() => {
        onBack();
      }, 2000);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="w-20 h-20 rounded-full bg-[#00B277] flex items-center justify-center mb-4 animate-scale-in">
          <CheckCircle2 className="w-12 h-12 text-white" />
        </div>
        <h2 className="text-[20px] text-[#111111] mb-2">Report Submitted</h2>
        <p className="text-[14px] text-[#6B6B6B] text-center">
          Our support team will review your report and contact you shortly
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button 
          onClick={onBack}
          className="w-12 h-12 flex items-center justify-center hover:bg-gray-100 rounded-xl transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft className="w-6 h-6 text-[#111111]" />
        </button>
        <div>
          <h1 className="text-[18px] text-[#111111]">Report Incident</h1>
          <p className="text-[12px] text-[#6B6B6B]">We're here to help</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Emergency Banner */}
        <div className="bg-[#E02020] rounded-[16px] p-4 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-white flex-shrink-0 mt-1" />
          <div>
            <p className="text-[14px] text-white mb-1">Emergency?</p>
            <p className="text-[12px] text-white/90 mb-2">
              If you're in immediate danger, call emergency services first
            </p>
            <button className="h-10 px-4 rounded-[12px] bg-white text-[#E02020] text-[14px] hover:bg-white/90 transition-colors">
              Call 112
            </button>
          </div>
        </div>

        {/* Current Location & Time */}
        <div className="bg-white rounded-[16px] p-4 space-y-3">
          <div className="flex items-center gap-2 text-[#6B6B6B]">
            <MapPin className="w-4 h-4" />
            <p className="text-[12px]">Lekki Phase 1, Lagos</p>
          </div>
          <div className="flex items-center gap-2 text-[#6B6B6B]">
            <Clock className="w-4 h-4" />
            <p className="text-[12px]">Dec 8, 2025 • 2:15 PM</p>
          </div>
        </div>

        {/* Incident Type */}
        <div>
          <label className="block text-[14px] text-[#111111] mb-2">
            Incident Type <span className="text-[#E02020]">*</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {incidentTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => setIncidentType(type.value)}
                className={`h-14 rounded-[12px] border-2 transition-all ${
                  incidentType === type.value
                    ? 'border-[#E0001B] bg-[#E0001B]/5'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span className="text-[20px]">{type.icon}</span>
                  <span className="text-[12px] text-[#111111]">{type.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-[14px] text-[#111111] mb-2">
            Description <span className="text-[#E02020]">*</span>
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Please describe what happened in detail..."
            rows={6}
            className="w-full px-4 py-3 rounded-[12px] border-2 border-gray-200 bg-white focus:border-[#E0001B] focus:outline-none transition-colors resize-none text-[14px]"
          />
          <p className="text-[12px] text-[#6B6B6B] mt-1">
            {description.length}/500 characters
          </p>
        </div>

        {/* Photo Upload */}
        <div>
          <label className="block text-[14px] text-[#111111] mb-2">
            Photos (Optional)
          </label>
          
          <div className="grid grid-cols-3 gap-2">
            {photos.map((photo, index) => (
              <div key={index} className="aspect-square relative">
                <img
                  src={photo}
                  alt={`Incident ${index + 1}`}
                  className="w-full h-full object-cover rounded-[12px] border border-gray-200"
                />
                <button
                  onClick={() => setPhotos(photos.filter((_, i) => i !== index))}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-[#E02020] rounded-full text-white flex items-center justify-center text-[14px]"
                >
                  ×
                </button>
              </div>
            ))}
            
            {photos.length < 3 && (
              <button
                onClick={handlePhotoCapture}
                className="aspect-square border-2 border-dashed border-gray-300 rounded-[12px] flex flex-col items-center justify-center gap-1 hover:border-[#E0001B] hover:bg-[#E0001B]/5 transition-colors"
              >
                <Camera className="w-6 h-6 text-[#6B6B6B]" />
                <span className="text-[10px] text-[#6B6B6B]">Add Photo</span>
              </button>
            )}
          </div>
          
          <p className="text-[12px] text-[#6B6B6B] mt-2">
            Add up to 3 photos to help us understand the situation
          </p>
        </div>

        {/* Safety Tips */}
        <div className="bg-blue-50 border border-blue-200 rounded-[16px] p-4">
          <h3 className="text-[14px] text-blue-900 mb-2">Safety Tips</h3>
          <ul className="space-y-1 text-[12px] text-blue-700">
            <li>• Move to a safe location if possible</li>
            <li>• Don't engage in confrontations</li>
            <li>• Contact local authorities if needed</li>
            <li>• Keep your evidence secure</li>
          </ul>
        </div>

        {/* API Endpoint Annotation */}
        <div className="bg-blue-50 border border-blue-200 rounded-[12px] p-3">
          <p className="text-[10px] text-blue-900 mb-1">API ENDPOINT</p>
          <div className="space-y-1 text-[10px] text-blue-700 font-mono">
            <p>POST /rider/:id/incidents</p>
            <p>Body: type, description, photos[], location, timestamp</p>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <button
          onClick={handleSubmit}
          disabled={!incidentType || !description}
          className="w-full h-12 rounded-[12px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <Upload className="w-5 h-5" />
          Submit Report
        </button>
      </div>
    </div>
  );
}